import type { AuthFlowPayload } from '@/store/state'

const STORAGE_KEY = 'escs_oauth_auth_flow_payload'

/**
 * Session-scoped persistence of the BRAND-relevant subset of the OAuth
 * authorize payload, so the brand theme (see brand-theme.ts) survives a hard
 * reload mid sign-in.
 *
 * SECURITY INVARIANTS — both were violated once and fixed; keep them:
 * 1. Only the whitelisted cosmetic fields below are ever written. The full
 *    authFlowPayload carries `ssoToken` and `code`, which are LIVE LOGIN
 *    CREDENTIALS (actions.js exchanges ssoToken via signInWithSSO) and must
 *    never reach sessionStorage.
 * 2. The persisted copy is never committed back into Vuex. `isOAuthFlow`
 *    keys on `authFlowPayload != null`, so rehydrating at boot re-arms an
 *    abandoned OAuth flow tab-wide (a later unrelated login would bounce to
 *    the stale flow's redirect_url with a fresh auth code). Read it only at
 *    the branding call site (Layout.vue).
 */
export type PersistedAuthFlowBrand = Pick<
	AuthFlowPayload,
	'type' | 'intent' | 'applicationId' | 'serviceName'
>

export function persistAuthFlowPayload(payload: AuthFlowPayload | null): void {
	try {
		if (payload == null) {
			sessionStorage.removeItem(STORAGE_KEY)
			return
		}
		const brandSubset: PersistedAuthFlowBrand = {
			type: payload.type,
			intent: payload.intent,
			applicationId: payload.applicationId,
			serviceName: payload.serviceName,
		}
		sessionStorage.setItem(STORAGE_KEY, JSON.stringify(brandSubset))
	} catch {
		// sessionStorage can throw in locked-down/private-mode webviews (some
		// in-app browsers). The flow still works without persistence — it just
		// loses reload safety, same as before this module existed.
	}
}

export function readPersistedAuthFlowPayload(): PersistedAuthFlowBrand | null {
	try {
		const raw = sessionStorage.getItem(STORAGE_KEY)
		return raw ? JSON.parse(raw) : null
	} catch {
		return null
	}
}
